import { BUSINESS_PLAN_TYPES } from 'constants/businessPlan';
import { t } from 'i18next';
import { icons } from 'PDF/assets/icons';
import { getColorType } from 'PDF/helpers/documentStyle';
import { formatDateForPDF } from 'PDF/helpers/formatDates';
import { formatCurrency } from 'PDF/helpers/formatMoneyPDF';
import { formatMonth } from 'PDF/helpers/formatMonthPDF';
import { formatPercentage } from 'PDF/helpers/formatPercentagePDF';
import { getMagazineTextObject, IInfoTableRow } from 'PDF/helpers/pdfMapping';

import { IBusinessPlanType } from 'interfaces/businessPlan';

import { IBusinessToolForm } from 'components/BusinessToolForm/utils/form';

import { getOperatingIndustry } from 'helpers/businessTool/mappers/getOperatingIndustry';
import { getCurrencySymbol } from 'helpers/getCurrencySymbol';

export const getMagazineItems = (
  businessPlanChapters: IBusinessToolForm,
  isInvestor: boolean,
  isExamplePage: boolean,
) => {
  const {
    mainGoals,
    projectBriefDescription,
    strategyForAchievingGoals,
    companyName,
    registrationDate,
    address,
    mission,
    mainProductsOrServices,
    competitionAdvantage,
    productDevelopmentPlans,
    targetMarket,
    targetAudience,
    targetChannels,
    mainCompetitors,
    ...rest
  } = businessPlanChapters.GLOBAL;
  const { companyDescription, productsOrServices, marketAndCustomers, organizationAndTeam, risks } =
    businessPlanChapters.EXECUTIVE_SUMMARY;

  return [
    [
      getMagazineTextObject(
        isExamplePage ? 'Main Goals and Objectives. ' : t('pdfPages.mappings.summary.mainGoals'),
        mainGoals,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Description of the Project. ' : t('pdfPages.mappings.summary.descriptionProject'),
        projectBriefDescription,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Strategy for Project Success. ' : t('pdfPages.mappings.summary.strategySuccess'),
        strategyForAchievingGoals,
      ),
    ],
    [
      getMagazineTextObject(isExamplePage ? 'Name. ' : t('pdfPages.mappings.summary.name'), companyName),
      getMagazineTextObject(
        isExamplePage ? 'Date of Registration. ' : t('pdfPages.mappings.summary.registrationDate'),
        formatDateForPDF(registrationDate, 'MM.DD.YYYY', false),
      ),
      getMagazineTextObject(
        isExamplePage ? 'Operating Industry. ' : t('pdfPages.mappings.summary.operatingIndustry'),
        getOperatingIndustry(rest),
      ),
      getMagazineTextObject(isExamplePage ? 'Address. ' : t('pdfPages.mappings.summary.address'), address),
      getMagazineTextObject(
        isExamplePage ? 'Owners of the Company. ' : t('pdfPages.mappings.summary.owners'),
        companyDescription.companyOwners,
      ),
      getMagazineTextObject(
        isExamplePage ? 'History of the Business. ' : t('pdfPages.mappings.summary.businessHistory'),
        companyDescription.history,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Company Mission. ' : t('pdfPages.mappings.summary.companyMission'),
        mission,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Main Resources and Assets. ' : t('pdfPages.mappings.summary.mainResources'),
        companyDescription.resourcesAndAssets,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Main Products/Services Offered. ' : t('pdfPages.mappings.summary.mainProducts'),
        mainProductsOrServices,
      ),
      getMagazineTextObject(
        isExamplePage
          ? 'Product/Service Value and Problem Solved. '
          : t('pdfPages.mappings.summary.productValuesAndProblems'),
        productsOrServices.productsValue,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Unique Features or Competitive Advantage. ' : t('pdfPages.mappings.summary.uniqueFeatures'),
        competitionAdvantage,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Product/Service Development Plans. ' : t('pdfPages.mappings.summary.productDevelopmentPlan'),
        productDevelopmentPlans,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Target Market and Consumption. ' : t('pdfPages.mappings.summary.targetMarket'),
        targetMarket,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Market Dynamics. ' : t('pdfPages.mappings.summary.marketDynamics'),
        marketAndCustomers.dynamics,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Target Audience. ' : t('pdfPages.mappings.summary.targetAudience'),
        targetAudience,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Sales Channels. ' : t('pdfPages.mappings.summary.salesChannels'),
        targetChannels,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Main Competitors. ' : t('pdfPages.mappings.summary.mainCompetitors'),
        mainCompetitors,
      ),
      !isInvestor &&
        getMagazineTextObject(
          isExamplePage ? 'Competition Strategy. ' : t('pdfPages.mappings.summary.competitionStrategy'),
          marketAndCustomers.competitionPlan,
        ),
    ].filter(Boolean),
    [
      getMagazineTextObject(
        isExamplePage ? 'Core Team Members. ' : t('pdfPages.mappings.summary.coreTeam'),
        organizationAndTeam.members,
      ),
    ],
    [
      getMagazineTextObject(
        isExamplePage ? 'Main Project Risks. ' : t('pdfPages.mappings.summary.mainProjectRisks'),
        risks.mainRisks,
      ),
      getMagazineTextObject(
        isExamplePage ? 'Risk Mitigation Measures. ' : t('pdfPages.mappings.summary.riskMitigation'),
        risks.measures,
      ),
    ],
  ];
};

export const getFinancialInformation = (
  businessPlanChapters: IBusinessToolForm,
  businessPlanType: IBusinessPlanType,
  isExamplePage: boolean,
): IInfoTableRow[] => {
  const { annualIncome, projectedAnnualIncome, forecastedProfitability, projectPaybackPeriod, currency } =
    businessPlanChapters.GLOBAL;

  const isInvestor = businessPlanType === BUSINESS_PLAN_TYPES.FOR_INVESTORS;

  const currencySymbol = getCurrencySymbol(currency);

  const { returnOnInvestment, averageAnnualProfit, projectedAnnualProfit, internalRateOfReturn, assetsSize } =
    businessPlanChapters.EXECUTIVE_SUMMARY.financialInformation;

  const {
    IRRIcon,
    PastAnnualIncomeIcon,
    PastAnnualProfit,
    PPPIcon,
    ProjectedAnnualIncomeIcon,
    ProjectedAnnualProfitIcon,
    ProjectedProfitabilityIcon,
    ROIIcon,
    SizeOfYourAssetsIcon,
  } = icons[getColorType()];

  return [
    {
      id: 'annualIncome',
      info: annualIncome ? formatCurrency(+annualIncome, currencySymbol) : '',
      title: isExamplePage ? 'Past Annual Income' : t('pdfPages.mappings.summary.pastAnnualIncome'),
      iconSrc: PastAnnualIncomeIcon,
    },
    {
      id: 'averageAnnualProfit',
      info: averageAnnualProfit ? formatCurrency(+averageAnnualProfit, currencySymbol) : '',
      title: isExamplePage ? 'Past Annual Profit' : t('pdfPages.mappings.summary.pastAnnualProfit'),
      iconSrc: PastAnnualProfit,
    },
    isInvestor
      ? ({} as IInfoTableRow)
      : {
          id: 'assetsSize',
          info: assetsSize ? formatCurrency(+assetsSize, currencySymbol) : '',
          title: isExamplePage ? 'Size of Your Assets' : t('pdfPages.mappings.summary.sizeOfAssets'),
          iconSrc: SizeOfYourAssetsIcon,
        },
    {
      id: 'projectedAnnualIncome',
      info: projectedAnnualIncome ? formatCurrency(+projectedAnnualIncome, currencySymbol) : '',
      title: isExamplePage ? 'Projected Annual Income' : t('pdfPages.mappings.summary.projAnnualIncome'),
      iconSrc: ProjectedAnnualIncomeIcon,
    },
    {
      id: 'projectedAnnualProfit',
      info: projectedAnnualProfit ? formatCurrency(+projectedAnnualProfit, currencySymbol) : '',
      title: isExamplePage ? 'Projected Annual Profit' : t('pdfPages.mappings.summary.projAnnualProfit'),
      iconSrc: ProjectedAnnualProfitIcon,
    },
    {
      id: 'forecastedProfitability',
      info: forecastedProfitability ? formatPercentage(forecastedProfitability) : '',
      title: isExamplePage ? 'Projected Profitability' : t('pdfPages.mappings.summary.projProfitability'),
      iconSrc: ProjectedProfitabilityIcon,
    },
    {
      id: 'projectPaybackPeriod',
      info: projectPaybackPeriod ? formatMonth(projectPaybackPeriod, isExamplePage) : '',
      title: isExamplePage ? 'Projected Payback Period (PPP)' : t('pdfPages.mappings.summary.projPayBackPeriod'),
      iconSrc: PPPIcon,
    },
    {
      id: 'returnOnInvestment',
      info: returnOnInvestment ? formatPercentage(returnOnInvestment) : '',
      title: isExamplePage ? 'Return of Investment (ROI)' : t('pdfPages.mappings.summary.roi'),
      iconSrc: ROIIcon,
    },
    {
      id: 'internalRateOfReturn',
      info: internalRateOfReturn ? formatPercentage(internalRateOfReturn) : '',
      title: isExamplePage ? 'Internal Rate of Return (IRR) ' : t('pdfPages.mappings.summary.irr'),
      iconSrc: IRRIcon,
    },
  ].filter(({ info }) => info);
};

export const getFinancingNeeds = (businessPlanChapters: IBusinessToolForm, isInvestor: boolean, isExamplePage: boolean): IInfoTableRow[] => {
  const { researchCost, marketingCost, workingCapitalCost, equipmentCost, constructionCost, otherCost } =
    businessPlanChapters.EXECUTIVE_SUMMARY.financingNeeds;
  const { totalCost, fundingAmount, percentageOfFunding, investmentAmount, loanBorrowAmount, currency } =
    businessPlanChapters.GLOBAL;
  const {
    CurrentCostIcon,
    FundingByOwnFundsIcon,
    FundingDirectionsIcon,
    FundingNeedsIcon,
    InvestmentRaisedIcon,
    LoanAmountPlannedIcon,
  } = icons[getColorType()];
  const currencySymbol = getCurrencySymbol(currency);

  return [
    {
      id: 'totalCost',
      info: totalCost ? formatCurrency(+totalCost, currencySymbol) : '',
      title:isExamplePage ? 'Total Project Cost' : t('pdfPages.mappings.summary.totalProjCost'),
      iconSrc: CurrentCostIcon,
    },
    {
      id: 'fundingNeeds',
      info: fundingAmount ? formatCurrency(+fundingAmount, currencySymbol) : '',
      title:isExamplePage ? 'Funding Needs' : t('pdfPages.mappings.summary.fundingNeeds'),
      iconSrc: FundingNeedsIcon,
    },
    {
      id: 'percentageOfFunding',
      info: percentageOfFunding ? formatPercentage(percentageOfFunding || '') : '',
      title:isExamplePage ? 'Funding by Own Funds' : t('pdfPages.mappings.summary.fundingByOwnFund'),
      iconSrc: FundingByOwnFundsIcon,
    },
    {
      id: 'loanBorrowAmount',
      info: loanBorrowAmount && !isInvestor ? formatCurrency(+loanBorrowAmount, currencySymbol) : '',
      title:isExamplePage ? 'Loan Amount Planned' : t('pdfPages.mappings.summary.loanAmountPlanned'),
      iconSrc: LoanAmountPlannedIcon,
    },
    {
      id: 'investmentAmount',
      info: investmentAmount && !isInvestor ? formatCurrency(+investmentAmount, currencySymbol) : '',
      title:isExamplePage ? 'Investment Planned' : t('pdfPages.mappings.summary.investPlanned'),
      iconSrc: InvestmentRaisedIcon,
    },
    {
      id: 'fundingDirections',
      title:isExamplePage ? 'Funding Directions' : t('pdfPages.mappings.summary.findDirections'),
      iconSrc: FundingDirectionsIcon,
      info:
        [
          {
            info: formatPercentage(researchCost?.percentage || ''),
            title:isExamplePage ? 'Research and Development (R&D)' : t('executiveSummary.financialNeeds.researchCost'),
          },
          {
            info: formatPercentage(marketingCost?.percentage || ''),
            title:isExamplePage ? 'Marketing and Sales' : t('executiveSummary.financialNeeds.marketingCost'),
          },
          {
            info: formatPercentage(workingCapitalCost?.percentage || ''),
            title:isExamplePage ? 'Working Capital' : t('executiveSummary.financialNeeds.workingCapitalCost'),
          },
          {
            info: formatPercentage(constructionCost?.percentage || ''),
            title:isExamplePage ? 'Construction of Buildings and Facilities' : t('executiveSummary.financialNeeds.constructionCost'),
          },
          {
            info: formatPercentage(equipmentCost?.percentage || ''),
            title:isExamplePage ? 'Purchase of Equipment' : t('executiveSummary.financialNeeds.equipmentCost'),
          },
          {
            info: formatPercentage(otherCost?.percentage || ''),
            title:isExamplePage ? 'Other' : t('executiveSummary.financialNeeds.otherCost'),
          },
        ]
          .filter(({ info }) => info)
          ?.map(({ info, title }) => `${title} - ${info}`) || [],
    },
  ].filter(({ info }) => info);
};
